import React, {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react'
import { FloatingLabel, Form } from 'react-bootstrap'

interface TextInputProps {
    label: string
    onChange: (value: string) => Promise<void>
    value: string
    errorMessages: Array<string>
    placeholder?: string
}

// Debounce delay (ms) before triggering the DB write.
// Prevents PouchDB 409 conflicts from rapid keystrokes.
const DEBOUNCE_MS = 300

const TextInput: React.FC<TextInputProps> = ({
    label,
    onChange,
    value,
    errorMessages,
    placeholder,
}) => {
    const id = useId()

    // Local state keeps the UI responsive on every keystroke
    const [localValue, setLocalValue] = useState(value)
    const [isFocused, setIsFocused] = useState<boolean>(false)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Ref avoids stale closure: always calls the latest onChange
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    // Sync local state from parent (e.g. when doc updates from DB)
    useEffect(() => {
        setLocalValue(value)
    }, [value])

    const floatingLabelClassName = useMemo<string>(() => {
        const classNames = []

        if (isFocused || localValue) {
            classNames.push('text-area-expanded')
        }

        if (localValue) {
            classNames.push('label-hidden')
        }

        return classNames.join(' ')
    }, [isFocused, localValue])

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newValue = event.target.value
            // Update UI immediately — no data loss
            setLocalValue(newValue)

            // Reset the debounce timer on each keystroke so only the
            // final value after the user pauses is written to PouchDB
            if (timerRef.current) {
                clearTimeout(timerRef.current)
            }
            timerRef.current = setTimeout(() => {
                void onChangeRef.current(newValue)
            }, DEBOUNCE_MS)
        },
        [],
    )

    // Clean up pending timer on unmount to avoid memory leaks
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
            }
        }
    }, [])

    return (
        <FloatingLabel
            controlId={id}
            className={floatingLabelClassName}
            label={label}
        >
            <Form.Control
                as="textarea"
                onChange={handleChange}
                placeholder={placeholder}
                value={localValue}
                isInvalid={errorMessages.length > 0}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
            />
            {errorMessages.length > 0 && (
                <Form.Control.Feedback type="invalid">
                    {errorMessages.join(' ')}
                </Form.Control.Feedback>
            )}
        </FloatingLabel>
    )
}

export default TextInput
